/**
 * GH#1643 — el menú de la agencia en el MISMO ORDEN que GoHighLevel (pedido de
 * Randhy: la paridad incluye dónde está cada cosa). Orden sacado del issue, que
 * lo toma de las capturas del relevamiento (sub-cuenta Comercialo).
 *
 * Reglas:
 *  - Los bloques GHL van primero y en el orden exacto de GHL.
 *  - Lo nuestro (inmobiliario, ERP) va en un bloque propio DEBAJO, sin mezclarse.
 *  - Configuración queda abajo del todo (el Sidebar la fija fuera del scroll).
 *  - Lo que todavía no tiene pantalla lleva `proximamente` y NO tiene enlace:
 *    nunca un 404.
 *
 * Es sólo datos: el test de este archivo verifica que cada `href` exista en
 * `agencyRoutes.tsx`, así un ítem no puede apuntar a una ruta inexistente.
 */
import type { ComponentType } from 'react';
import {
  Home,
  MessageSquare,
  Calendar,
  Users,
  TrendingUp,
  CreditCard,
  Sparkles,
  Megaphone,
  Repeat,
  Globe,
  Repeat2,
  Image,
  Star,
  BarChart3,
  Settings,
  Building2,
  UserCog,
  ClipboardList,
  List,
  GitBranch,
  ScrollText,
  Receipt,
  FileText,
  LayoutTemplate,
  Palette,
  MessageCircle,
  Key,
  Shield,
  AlertTriangle,
  DollarSign,
} from 'lucide-react';

export interface NavItemGhl {
  name: string;
  /** Ausente si `proximamente`. */
  href?: string;
  icon: ComponentType<{ className?: string }>;
  proximamente?: boolean;
  /** Etiqueta junto al nombre de un ítem que SÍ tiene enlace (GH#2443: «Reportes» del agente, cuya pantalla dice «Próximamente»). */
  etiqueta?: string;
  /** Otras rutas que también marcan este ítem como activo (p. ej. las pestañas de Marketing). */
  activoEn?: string[];
  /**
   * Roles que pueden abrir `href`: los MISMOS de la ruta en el ruteo (el test lo
   * verifica). Sin esto, una pestaña manda a /unauthorized a quien no tiene la ruta.
   */
  roles?: string[];
}

export interface SeccionNavGhl {
  id: string;
  /** Vacío = sin encabezado. */
  titulo: string;
  items: NavItemGhl[];
}

/** Pestañas de Marketing, en el orden de GHL. */
export const PESTANAS_MARKETING: NavItemGhl[] = [
  {
    name: 'Planificador de redes sociales',
    href: '/agencia/redes/publicaciones',
    icon: Megaphone,
    roles: ['AGENCY_ADMIN', 'AGENCY_AGENT_USER'],
  },
  {
    name: 'Correos electrónicos',
    href: '/agencia/email-campaigns',
    icon: Megaphone,
    roles: ['AGENCY_ADMIN'], // = ROLES_EMAIL_CAMPAIGNS (GH#1631)
  },
  { name: 'Fragmentos', icon: Megaphone, proximamente: true },
  { name: 'Temporizadores de cuenta atrás', icon: Megaphone, proximamente: true },
  { name: 'Enlaces de activación', icon: Megaphone, proximamente: true },
  { name: 'Paneles de marca', icon: Megaphone, proximamente: true },
  {
    // Plan Ads Watley (decisión CTO [A], 23-oct): la pestaña lleva a /agencia/redes/ads, que es la
    // pantalla con las campañas REALES de Google Ads y el alta Google/Meta. /agencia/publicidad
    // (resultados de Meta) sigue alcanzable desde ahí con un enlace, para los roles de su ruta.
    name: 'Administrador de anuncios',
    href: '/agencia/redes/ads',
    icon: Megaphone,
    // Iguales a la ruta. ACCOUNTING_TEAM afuera (decisión CTO, 23-oct): el back no le da ninguna
    // ruta de Ads, ni de lectura, y la pantalla le respondía 403.
    roles: ['AGENCY_ADMIN', 'BROKER_MANAGER_ADMIN'],
  },
  { name: 'Prospección', icon: Megaphone, proximamente: true },
];

/**
 * Las pestañas que ve `role`: las que puede abrir, más las «Próximamente» (sin
 * enlace, no mandan a ningún lado). Sin rol, sólo las «Próximamente».
 */
export function pestanasParaRol(role: string | undefined): NavItemGhl[] {
  return PESTANAS_MARKETING.filter(
    p => p.proximamente || !p.roles || (!!role && p.roles.includes(role))
  );
}

const rutasMarketing = PESTANAS_MARKETING.filter(p => p.href).map(p => p.href as string);

export const SECCIONES_AGENCIA_GHL: SeccionNavGhl[] = [
  {
    id: 'ghl-1',
    titulo: '',
    items: [
      { name: 'Tablero', href: '/agencia/dashboard', icon: Home },
      { name: 'Conversaciones', href: '/agencia/inbox', icon: MessageSquare },
      { name: 'Calendarios', href: '/agencia/agenda', icon: Calendar },
      { name: 'Contactos', href: '/agencia/contactos', icon: Users },
      { name: 'Clientes potenciales', href: '/agencia/crm', icon: TrendingUp },
      { name: 'Pagos', href: '/agencia/enlaces-pago', icon: CreditCard },
    ],
  },
  {
    id: 'ghl-2',
    titulo: '',
    items: [
      { name: 'Agentes de IA', href: '/agencia/asistente-ia', icon: Sparkles, activoEn: ['/agencia/widget'] },
      { name: 'Marketing', href: '/agencia/redes/publicaciones', icon: Megaphone, activoEn: rutasMarketing },
      { name: 'Automatización', href: '/agencia/secuencias', icon: Repeat },
      { name: 'Sitios', href: '/agencia/builder', icon: Globe },
      { name: 'Suscripciones', icon: Repeat2, proximamente: true },
      { name: 'Contenido multimedia', icon: Image, proximamente: true },
      { name: 'Reputación', icon: Star, proximamente: true },
      { name: 'Informes', href: '/agencia/reportes', icon: BarChart3 },
    ],
  },
  {
    // Lo nuestro, que GHL no tiene o tiene en otra forma. Debajo y separado.
    id: 'inbomarket',
    titulo: 'InboMarket',
    items: [
      { name: 'Propiedades', href: '/agencia/inventario', icon: Building2 },
      { name: 'Agentes', href: '/agencia/agentes', icon: UserCog },
      { name: 'Clientes', href: '/agencia/clientes', icon: Users },
      { name: 'Leads', href: '/agencia/crm/leads', icon: List },
      { name: 'Embudos', href: '/agencia/crm/embudos', icon: GitBranch },
      { name: 'Tareas', href: '/agencia/tareas', icon: ClipboardList },
      { name: 'Contratos', href: '/agencia/documentos', icon: ScrollText },
      { name: 'Finanzas y DGII', href: '/agencia/erp', icon: Receipt },
      { name: 'Nuevo documento fiscal', href: '/agencia/erp/wizard', icon: FileText },
      // ERP fiscal (/erp/*). El menú agrupado se los mostraba al AGENCY_ADMIN y
      // «Finanzas y DGII» (/agencia/erp) NO enlaza a /erp/: sin estos ocho el admin
      // perdía el acceso a asientos, 607, e-CF, P12, NCF y plan contable.
      { name: 'ERP', href: '/erp/dashboard', icon: Receipt },
      { name: 'Asientos', href: '/erp/journal-entries', icon: ClipboardList },
      { name: 'Reportes DGII', href: '/erp/reports/607', icon: ScrollText },
      { name: 'Cola e-CF', href: '/erp/dgii/ecf', icon: FileText },
      { name: 'Certificados P12', href: '/erp/dgii/p12', icon: Shield },
      { name: 'Rangos NCF', href: '/erp/dgii/ncf', icon: AlertTriangle },
      { name: 'Plan contable', href: '/erp/cuentas', icon: LayoutTemplate },
      { name: 'Mis comisiones', href: '/erp/comisiones', icon: DollarSign },
      { name: 'Campañas', href: '/agencia/redes', icon: Megaphone },
      { name: 'Plantillas WhatsApp', href: '/agencia/whatsapp-plantillas', icon: MessageCircle },
      { name: 'Widget de chat', href: '/agencia/widget', icon: MessageSquare },
      { name: 'Ajustes de calendarios', href: '/agencia/calendarios', icon: Calendar },
      { name: 'Plantillas del sitio', href: '/agencia/templates', icon: LayoutTemplate },
      { name: 'Apariencia', href: '/agencia/apariencia', icon: Palette },
      { name: 'API Keys', href: '/agencia/api-keys', icon: Key },
    ],
  },
];

/** Fijo abajo del todo, como en GHL. */
export const CONFIGURACION_GHL: NavItemGhl = {
  name: 'Configuración',
  href: '/agencia/configuracion',
  icon: Settings,
};

/** Roles que ven el menú GHL. El resto sigue con el menú agrupado de siempre. */
export const ROLES_MENU_GHL = ['AGENCY_ADMIN'];

/** Largo de la ruta más específica de `item` que contiene a `pathname` (0 = ninguna). */
function largoCoincidencia(item: NavItemGhl, pathname: string): number {
  const rutas = [item.href, ...(item.activoEn ?? [])].filter(Boolean) as string[];
  return Math.max(0, ...rutas.filter(r => pathname === r || pathname.startsWith(`${r}/`)).map(r => r.length));
}

/**
 * El ÚNICO ítem activo para `pathname`: el de la coincidencia más larga. Sin esto,
 * `/agencia/crm/embudos` marcaba a la vez «Clientes potenciales» (/agencia/crm) y «Embudos».
 */
export function itemActivoDe(items: NavItemGhl[], pathname: string): NavItemGhl | undefined {
  let mejor: NavItemGhl | undefined;
  let largo = 0;
  for (const it of items) {
    const l = largoCoincidencia(it, pathname);
    if (l > largo) {
      largo = l;
      mejor = it;
    }
  }
  return mejor;
}

/** Todos los ítems del menú GHL, en orden, incluida Configuración. */
export const TODOS_LOS_ITEMS_GHL: NavItemGhl[] = [
  ...SECCIONES_AGENCIA_GHL.flatMap(s => s.items),
  CONFIGURACION_GHL,
];
